import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import {
  BOOKING_CANCELLED,
  BOOKING_UNSOLVED,
  BOOKING_CONFIRMED,
  BOOKING_PENDING,
} from 'src/constants/voucher'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'

import { sumGeneratedWallet } from './utils'
import { typePaymentMethods } from 'client/types/checkout'
import { typeVoucher } from 'client/types/voucher'

import { AncillariesBanner, SupportInfo } from '@s4l/uikit'
import BookingInformation from 'src/comps/BookingInformation'
import CollapsibleSection from 'client/components/CollapsibleSection/CollapsibleSection'
import Conditions from 'src/comps/Molecules/Conditions'
import HotelPreview from 'client/components/HotelPreview/HotelPreview'
import IconPlus from 'client/icons/Add'
import IconRemove from 'client/icons/Remove'
import InsuranceSection from 'client/components/Insurance/Section/InsuranceSection'
import PriceDetails from 'client/components/PriceDetails'
import { PriceDropProtection } from '../Molecules/PriceDropProtection'
import PrintButton from 'src/comps/PrintButton'
import Staywallet from 'src/comps/Staywallet'
import TravelInsurance from 'src/comps/TravelInsurance'
import VoucherBookingCodes from 'client/components/VoucherBookingCodes'
import VoucherBookingStatus from 'client/components/VoucherBookingStatus'
import VoucherGuestRoomListCta from 'client/components/VoucherGuestRoomListCta'
import VoucherGuestRoomListMessage from 'client/components/VoucherGuestRoomListMessage'
import VoucherGuestRoomListModal from 'client/components/VoucherGuestRoomListModal'
import VoucherGuestRoomListStatus from 'client/components/VoucherGuestRoomListStatus'
import VoucherInfoModal from 'src/comps/VoucherInfoModal'

import {
  CancelledBooking,
  CheckinSection,
  PriceDetailsContainer,
  Row,
  RowHeader,
  StaywalletContainer,
  VoucherBase,
  ContactRow,
} from './styles'

import styles from './styles.module.css'

import messages from './messages'
import { typeProperty } from 'client/types'

const GUEST_ROOM_LIST_MODAL_KEY = 'guest-room-list'

const Voucher = ({ pageId, paymentMethods, purchaseId, voucher, property, supportInfoJSON }) => {
  const intl = useIntl()
  const { sendEvent: sendAncillaryClickEvent } = useKafkaEvent({
    page: pageId,
  })

  const [modalConfig, setModalConfig] = useState({ isOpen: false, key: null, bookNumber: null })

  const { bookingInformation, currency, hotelPreview, nights, priceSummary, roomsFullInformation } =
    voucher

  const statusOrder = {
    [BOOKING_CONFIRMED]: 1,
    [BOOKING_PENDING]: 1,
    [BOOKING_UNSOLVED]: 2,
    [BOOKING_CANCELLED]: 2,
  }

  const title = <CancelledBooking>{intl.formatMessage(messages.bookingCancelled)}</CancelledBooking>

  let isFormDisplayed = false

  const { insurance } = bookingInformation[0]
  const isMultiBooking = bookingInformation.length > 1
  const isPrime = priceSummary.isPrime || false
  const walletEarned = sumGeneratedWallet(bookingInformation)
  const showDiscount = priceSummary?.bigHead > 0
  const roomPrice = showDiscount ? priceSummary?.bigHead : priceSummary?.price
  const showPriceDropProtection = priceSummary.priceDropProtection > 0
  const voucherNumbersFiltered = bookingInformation
    .filter(item => item.bookingStatus !== BOOKING_CANCELLED)
    .map(item => item.voucher)

  const voucherNumbers =
    voucherNumbersFiltered.length > 0 ? voucherNumbersFiltered : [bookingInformation[0].voucher]

  const getTotalGuests = booking => {
    return (Number(booking.adults) + Number(booking.children)) * Number(booking.rooms)
  }
  const getFirstGuest = booking => {
    return booking.holders[0] || {}
  }

  const pendingRoomingListCount = bookingInformation.filter(item => item.isRoomingList).length
  const haveSomeRoomingList = pendingRoomingListCount > 0
  const totalGuests = bookingInformation.reduce((acc, book) => acc + getTotalGuests(book), 0)

  const openInfoModal = key => {
    setModalConfig({ isOpen: true, key })
  }

  const openGuestRoomListModal = ({ bookNumber }) => {
    setModalConfig({ isOpen: true, key: GUEST_ROOM_LIST_MODAL_KEY, bookNumber })
  }

  const closeModal = () => {
    setModalConfig({ isOpen: false, key: null, bookNumber: null })
  }

  return (
    <VoucherBase data-testid="voucher-container">
      <section className={styles.bookingHeader}>
        <div>
          <VoucherBookingStatus
            location={hotelPreview?.propertyCity}
            bookingInformation={bookingInformation}
            onClick={openInfoModal}
          >
            <VoucherGuestRoomListStatus isEnabled={haveSomeRoomingList}>
              {bookingInformation.map((book, index) => (
                <VoucherGuestRoomListCta
                  key={book.hash}
                  bookNumber={index + 1}
                  isEnabled={book.isRoomingList}
                  isMultiBooking={isMultiBooking}
                  onClick={openGuestRoomListModal}
                  totalGuests={getTotalGuests(book)}
                />
              ))}
            </VoucherGuestRoomListStatus>
          </VoucherBookingStatus>
        </div>

        <VoucherBookingCodes
          voucherNumbers={voucherNumbers}
          purchaseId={purchaseId}
          onClick={openInfoModal}
        />
      </section>

      <Row hidden={!haveSomeRoomingList}>
        <VoucherGuestRoomListMessage isEnabled={haveSomeRoomingList} totalGuests={totalGuests}>
          {bookingInformation.map((book, index) => (
            <VoucherGuestRoomListCta
              key={book.hash}
              bookNumber={index + 1}
              isEnabled={book.isRoomingList}
              isMultiBooking={isMultiBooking}
              onClick={openGuestRoomListModal}
              totalGuests={getTotalGuests(book)}
            />
          ))}
        </VoucherGuestRoomListMessage>
      </Row>

      <RowHeader>
        <PrintButton>{intl.formatMessage({ id: 'voucher.print-voucher' })}</PrintButton>
      </RowHeader>

      <Row>
        <HotelPreview hotelPreview={hotelPreview} property={property} />
        <PriceDetailsContainer className={priceSummary?.additionalFees?.length ? '' : 'noPrint'}>
          <PriceDetails
            additionalProducts={priceSummary.additionalProducts}
            nights={nights}
            roomsFullInformation={roomsFullInformation}
            total={priceSummary?.total}
            sellingRate={priceSummary?.price}
            sellingRateCurrency={currency}
            totalCurrency={currency}
            bighead={roomPrice}
            bigheadCurrency={currency}
            taxes={priceSummary?.taxes}
            taxesCurrency={currency}
            staywallet={priceSummary?.wallet}
            staywalletCurrency={currency}
            subtotal={priceSummary?.subtotal}
            subtotalCurrency={currency}
            additionalFees={priceSummary?.additionalFees}
            insurance={priceSummary?.insurance}
            insuranceCurrency={priceSummary?.currency}
            showPaymentDates={false}
            pdp={priceSummary?.priceDropProtection}
            pdpCurrency={currency}
            isPrime={isPrime}
            isVoucher
            showDiscount={showDiscount}
            paymentPolicy={bookingInformation[0].rateName}
          />
        </PriceDetailsContainer>
      </Row>

      <ContactRow>
        <SupportInfo supportInfoJSON={supportInfoJSON} />
      </ContactRow>

      {insurance && (
        <Row>
          <InsuranceSection insurance={insurance} consumer={pageId} />
        </Row>
      )}

      {showPriceDropProtection && (
        <Row>
          <PriceDropProtection
            source="voucher"
            quotes={priceSummary.priceDropProtection}
            paymentDate={bookingInformation[0].cancellationPolicies[0]?.cancellationDate}
          />
        </Row>
      )}

      {walletEarned > 0 && (
        <Row>
          <StaywalletContainer>
            <Staywallet amount={walletEarned} currency={currency} isEarned />
          </StaywalletContainer>
        </Row>
      )}

      {bookingInformation
        .sort((a, b) => statusOrder[a.bookingStatus] - statusOrder[b.bookingStatus])
        .map(booking => {
          if (booking.bookingStatus === BOOKING_CANCELLED) {
            return (
              <Row key={booking.hash}>
                <CollapsibleSection
                  fullWidth
                  title={title}
                  iconToOpen={IconPlus}
                  iconToClose={IconRemove}
                >
                  <BookingInformation
                    bookingInformation={booking}
                    displayForm={false}
                    paymentMethods={paymentMethods}
                    propertyId={hotelPreview?.propertyId}
                    purchaseId={purchaseId}
                    rooms={booking.rooms}
                  />
                </CollapsibleSection>
              </Row>
            )
          }

          const displayForm =
            !isFormDisplayed &&
            booking.bookingStatus === 'confirmed' &&
            (booking.paymentStatus === 'failed' || booking.paymentStatus === 'pending')

          if (displayForm) {
            isFormDisplayed = true
          }

          return (
            <>
              <Row key={booking?.idBooking}>
                <BookingInformation
                  bookingInformation={booking}
                  displayForm={displayForm}
                  paymentMethods={paymentMethods}
                  propertyId={hotelPreview?.propertyId}
                  purchaseId={purchaseId}
                  rooms={booking.rooms}
                  isRefundable={roomsFullInformation?.[0]?.isRefundable}
                />
              </Row>
              {booking.travelInsurance && (
                <Row>
                  <TravelInsurance.Summary
                    insuranceTypeId={booking.travelInsurance.type}
                    currency={booking.travelInsurance.currency}
                  />
                </Row>
              )}
            </>
          )
        })}

      {hotelPreview?.conditions && Object.keys(hotelPreview?.conditions).length > 0 && (
        <Row>
          <CheckinSection>
            <Conditions {...hotelPreview?.conditions} onModal={false} />
          </CheckinSection>
        </Row>
      )}

      {voucher.ancillaries && (
        <Row className="noPrint">
          <div className={styles.ancillaries}>
            <AncillariesBanner
              ancillariesJSON={{ list: voucher.ancillaries }}
              onClick={({ type }) =>
                sendAncillaryClickEvent(
                  {
                    section: SECTIONS[`ANCILLARIES_${type.toUpperCase()}`],
                  },
                  { ancillaryOrigin: 'voucher' }
                )
              }
            />
          </div>
        </Row>
      )}

      <VoucherInfoModal
        item={modalConfig.key}
        isOpen={modalConfig.isOpen && modalConfig.key !== GUEST_ROOM_LIST_MODAL_KEY}
        onClose={closeModal}
      />

      {bookingInformation.map((book, index) => (
        <VoucherGuestRoomListModal
          key={book.hash}
          hash={book.hash}
          bookNumber={index + 1}
          initialGuest={getFirstGuest(book)}
          isOpen={
            modalConfig.isOpen &&
            modalConfig.key === GUEST_ROOM_LIST_MODAL_KEY &&
            modalConfig.bookNumber === index + 1
          }
          isMultiBooking={isMultiBooking}
          onClose={closeModal}
          pendingRoomingListCount={pendingRoomingListCount}
          purchaseId={purchaseId}
          totalGuests={getTotalGuests(book)}
        />
      ))}
    </VoucherBase>
  )
}

Voucher.propTypes = {
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paymentMethods: typePaymentMethods,
  purchaseId: PropTypes.string,
  voucher: typeVoucher,
  property: typeProperty,
  supportInfoJSON: PropTypes.instanceOf(Object),
}

export default Voucher
